import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { OAuthError, useAuth0, User } from '@auth0/auth0-react';
import { Copy } from '@globalvisioninc/assets/icons';
import { Button } from '@globalvisioninc/ui/button';
import { Navigate } from 'react-router-dom';
import { isSuperAdmin } from 'utils/auth';
import { app } from 'store';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import { GVTypography } from 'components/lib';
import { axiosInstance } from 'store/store';
import { useUserStateStore } from 'zstore/userStateStore';
import styles from './index.module.css';

const CopyButton = ({ text }: { text: string }) => {
  const dispatch = useDispatch();

  const handleClick = () => {
    navigator.clipboard.writeText(text);
    dispatch(app.actions.setSnackMessage({ message: 'Copied to Clipboard', type: 'success' }));
  };

  return (
    <Button.Root type="button" mode="tertiary" aria-label="Copy to clipboard" onClick={handleClick}>
      <Copy />
    </Button.Root>
  );
};

const InternalAPIBody = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const { user } = useUserStateStore();
  const [accessToken, setAccessToken] = useState('');

  useEffect(() => {
    try {
      getAccessTokenSilently().then((token) => setAccessToken(token));
    } catch (e: unknown) {
      if (e instanceof OAuthError && (e.error === 'missing_refresh_token' || e.error === 'invalid_grant')) {
        loginWithRedirect({
          appState: {
            returnTo: window.location.pathname,
          },
        });
      }
    }
  });

  // Redirect if user is not a tenantmanager or superadmin
  if (!isSuperAdmin(user as User)) {
    return <Navigate to="e" />;
  }

  const baseURL = `${window.location.protocol}//${window.location.hostname}${axiosInstance.defaults.baseURL}`;
  const jsonUser = JSON.stringify(user);

  return (
    <div className={styles.body}>
      <div className={styles.header}>
        <GVTypography variant="h4">
          This section contains useful information to work with Inspect Service APIs.
        </GVTypography>
        <GVTypography variant="subtitle1">To be used by `superadmin` users only</GVTypography>
      </div>

      <GVTextField
        elementEnd={<CopyButton text={baseURL} />}
        value={baseURL}
        placeholder="Base URL"
        label="Inspect Service Base URL"
        isLabelDynamic={false}
      />

      <GVTextField
        elementEnd={<CopyButton text={accessToken} />}
        value={accessToken}
        placeholder="Access Token"
        label="Access Token"
        isLabelDynamic={false}
      />

      <GVTextField
        elementEnd={<CopyButton text={jsonUser} />}
        value={jsonUser}
        placeholder="User"
        label="Auth0 User"
        isLabelDynamic={false}
      />
    </div>
  );
};

export default InternalAPIBody;
